/**
 * Toast viewport (NIT-013). Renders the `useToastStore` queue and
 * schedules auto-dismiss per toast. Mounts once in StationPage /
 * RegistryPage near the root.
 *
 * Each toast gets its own auto-dismiss timer scheduled in the
 * `useEffect` that registers it; the cleanup clears the timer when the
 * toast is removed or the viewport unmounts.
 */
import { useEffect } from 'react';
import { useToastStore } from '@/state/useToastStore';

const KIND_CLS: Record<string, string> = {
  success: 'border-primary-base bg-primary-base/80 text-primary-on',
  info: 'border-sky-700 bg-sky-950/80 text-sky-100',
  error: 'border-rose-700 bg-rose-950/80 text-rose-100',
};
const KIND_FALLBACK = KIND_CLS['info'] ?? KIND_CLS['success'] ?? '';

export function ToastViewport(): JSX.Element {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  const duration = useToastStore((s) => s.defaultDurationMs);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((t) => setTimeout(() => dismiss(t.id), duration));
    return () => {
      for (const id of timers) clearTimeout(id);
    };
  }, [toasts, duration, dismiss]);

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2"
      data-testid="toast-viewport"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto max-w-[92vw] break-words rounded border px-3 py-2 text-xs shadow ${KIND_CLS[t.kind] ?? KIND_FALLBACK}`}
          data-testid={`toast-${t.kind}`}
          role="status"
        >
          {t.text}
        </div>
      ))}
    </div>
  );
}
